import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import {
  getTime, patchTime,
  type TimeState, type TimePatchPayload, type DstSection,
} from "../../api/config";
import { TIMEZONES, timezoneLabel, DST_PRESET_CHILE } from "../../api/timezones";
import type { Device } from "../../types";

const WEEK_OPTS = [
  { value: "0", label: "Día del mes" },
  { value: "1", label: "1ª semana" },
  { value: "2", label: "2ª semana" },
  { value: "3", label: "3ª semana" },
  { value: "4", label: "4ª semana" },
  { value: "-1", label: "Última semana" },
];

const DAY_OPTS = [
  { value: "0", label: "Domingo" },
  { value: "1", label: "Lunes" },
  { value: "2", label: "Martes" },
  { value: "3", label: "Miércoles" },
  { value: "4", label: "Jueves" },
  { value: "5", label: "Viernes" },
  { value: "6", label: "Sábado" },
];

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"][i],
}));

const TZ_OPTS = TIMEZONES.map(t => ({
  value: String(t.value),
  label: t.hint ? `${t.label} — ${t.hint}` : t.label,
}));

// Formatea Date local a "YYYY-MM-DD HH:MM:SS" (sin zona horaria — el NVR lo interpreta como local del dispositivo).
function fmtLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Para input type="datetime-local" (sin segundos): "YYYY-MM-DDTHH:MM"
function toDtInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  deviceId: number;
  device: Device;
}

export function TimeTab({ deviceId, device: _device }: Props) {
  const [state, setState] = useState<TimeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [timeMode, setTimeMode] = useState<"now" | "custom" | "off">("off");
  const [customTime, setCustomTime] = useState<string>("");
  const [tz, setTz] = useState<number>(24);
  const [dst, setDst] = useState<TimeState["dst"]>({ Enable: false, Start: blankSection(), End: blankSection() });
  const [ntpEnable, setNtpEnable] = useState(false);
  const [ntpAddress, setNtpAddress] = useState("");
  const [ntpPort, setNtpPort] = useState(123);
  const [ntpPeriod, setNtpPeriod] = useState(60);

  const reload = async () => {
    setLoading(true);
    try {
      const s = await getTime(deviceId);
      setState(s);
      setTz(s.timezone);
      setDst(s.dst);
      setNtpEnable(s.ntp.Enable);
      setNtpAddress(s.ntp.Address);
      setNtpPort(s.ntp.Port);
      setNtpPeriod(s.ntp.UpdatePeriod);
      setTimeMode("off");
      setCustomTime(toDtInputValue(new Date()));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo hora");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [deviceId]);

  if (loading || !state) return <Spinner />;

  const updateDstSection = (which: "Start" | "End", patch: Partial<DstSection>) => {
    setDst(prev => ({ ...prev, [which]: { ...prev[which], ...patch } }));
  };

  const applyChilePreset = () => {
    setDst({
      ...DST_PRESET_CHILE,
      Start: { ...DST_PRESET_CHILE.Start, Year: new Date().getFullYear() },
      End:   { ...DST_PRESET_CHILE.End,   Year: new Date().getFullYear() + 1 },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: TimePatchPayload = {};
      if (timeMode === "now") {
        payload.time = fmtLocal(new Date());
      } else if (timeMode === "custom" && customTime) {
        // customTime viene como "YYYY-MM-DDTHH:MM" — agregar ":00" para segundos
        payload.time = customTime.replace("T", " ") + ":00";
      }
      if (tz !== state.timezone) {
        payload.timezone = tz;
      }
      if (JSON.stringify(dst) !== JSON.stringify(state.dst)) {
        payload.dst = dst;
      }
      const ntpChanged =
        ntpEnable !== state.ntp.Enable ||
        ntpAddress !== state.ntp.Address ||
        ntpPort !== state.ntp.Port ||
        ntpPeriod !== state.ntp.UpdatePeriod;
      if (ntpChanged) {
        payload.ntp = { Enable: ntpEnable, Address: ntpAddress, Port: ntpPort, UpdatePeriod: ntpPeriod };
      }
      if (!payload.time && payload.timezone === undefined && !payload.dst && !payload.ntp) {
        toast("Sin cambios para guardar");
        return;
      }
      const res = await patchTime(deviceId, payload);
      if (res.errors && res.errors.length > 0) {
        toast.error(`Aplicado con errores: ${res.errors.join("; ")}`);
      } else {
        toast.success("Hora y configuración actualizadas");
      }
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h2 className="text-base font-semibold">Hora y zona horaria</h2>
        <p className="text-xs text-muted mt-0.5">Sincronización de hora del NVR, zona horaria, horario de verano y NTP automático.</p>
      </div>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Estado actual</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-muted">Hora del NVR:</span> <span className="font-mono">{state.current_time}</span></div>
          <div><span className="text-muted">Zona horaria:</span> {timezoneLabel(state.timezone)} {state.timezone_desc && `(${state.timezone_desc})`}</div>
          <div><span className="text-muted">Horario de verano:</span> {state.dst.Enable ? "Activado" : "Desactivado"}</div>
          <div><span className="text-muted">NTP automático:</span> {state.ntp.Enable ? `Activado (${state.ntp.Address})` : "Desactivado"}</div>
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Sincronizar hora</h3>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" name="timeMode" checked={timeMode === "off"} onChange={() => setTimeMode("off")} />
            <span>No cambiar la hora</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="timeMode" checked={timeMode === "now"} onChange={() => setTimeMode("now")} />
            <span>Usar la hora de mi computadora ahora (<span className="font-mono text-xs">{fmtLocal(new Date())}</span>)</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="timeMode" checked={timeMode === "custom"} onChange={() => setTimeMode("custom")} />
            <span>Hora personalizada</span>
            <input
              type="datetime-local"
              className="bg-line border border-line rounded px-2 py-1 text-xs font-mono"
              value={customTime}
              onChange={(e) => { setCustomTime(e.target.value); setTimeMode("custom"); }}
            />
          </label>
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Zona horaria</h3>
        <Select
          label="Zona horaria"
          options={TZ_OPTS}
          value={String(tz)}
          onChange={(e) => setTz(Number(e.target.value))}
        />
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Horario de verano (DST)</h3>
          <Button size="sm" variant="ghost" onClick={applyChilePreset} type="button">
            Aplicar preset Chile
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={dst.Enable} onChange={(e) => setDst({ ...dst, Enable: e.target.checked })} />
          <span>Habilitar horario de verano</span>
        </label>
        <div className={dst.Enable ? "space-y-3" : "space-y-3 opacity-40 pointer-events-none"}>
          <DstSectionEditor label="Inicio" section={dst.Start} onChange={(p) => updateDstSection("Start", p)} />
          <DstSectionEditor label="Fin" section={dst.End} onChange={(p) => updateDstSection("End", p)} />
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Sincronización NTP automática</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={ntpEnable} onChange={(e) => setNtpEnable(e.target.checked)} />
          <span>Habilitar sincronización NTP</span>
        </label>
        <div className={ntpEnable ? "grid grid-cols-3 gap-3" : "grid grid-cols-3 gap-3 opacity-40 pointer-events-none"}>
          <Input
            label="Servidor"
            value={ntpAddress}
            onChange={(e) => setNtpAddress(e.target.value)}
            placeholder="pool.ntp.org"
          />
          <Input
            label="Puerto"
            type="number"
            min={1}
            max={65535}
            value={String(ntpPort)}
            onChange={(e) => setNtpPort(Number(e.target.value) || 123)}
          />
          <Input
            label="Frecuencia (min)"
            type="number"
            min={1}
            max={1440}
            value={String(ntpPeriod)}
            onChange={(e) => setNtpPeriod(Number(e.target.value) || 60)}
          />
        </div>
      </section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={save} loading={saving}>Guardar cambios</Button>
        <Button variant="ghost" onClick={reload} disabled={saving}>Restablecer</Button>
      </div>
    </div>
  );
}

function blankSection(): DstSection {
  return { Year: 0, Month: 1, Week: 0, Day: 0, Hour: 0, Minute: 0 };
}

interface DstSectionEditorProps {
  label: string;
  section: DstSection;
  onChange: (patch: Partial<DstSection>) => void;
}

export function DstSectionEditor({ label, section, onChange }: DstSectionEditorProps) {
  return (
    <div className="border border-line rounded p-2 bg-panel/20 space-y-2">
      <div className="text-xs font-semibold text-muted">{label}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Select
          label="Mes"
          options={MONTH_OPTS}
          value={String(section.Month || 1)}
          onChange={(e) => onChange({ Month: Number(e.target.value) })}
        />
        <Select
          label="Semana"
          options={WEEK_OPTS}
          value={String(section.Week)}
          onChange={(e) => onChange({ Week: Number(e.target.value) })}
        />
        <Select
          label="Día"
          options={DAY_OPTS}
          value={String(section.Day)}
          onChange={(e) => onChange({ Day: Number(e.target.value) })}
        />
        <Input
          label="Hora"
          type="number"
          min={0}
          max={23}
          value={String(section.Hour)}
          onChange={(e) => onChange({ Hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
        />
        <Input
          label="Minuto"
          type="number"
          min={0}
          max={59}
          value={String(section.Minute)}
          onChange={(e) => onChange({ Minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })}
        />
      </div>
    </div>
  );
}
